/**
 * DesignSubmissionsContext
 *
 * Provides the public gallery with approved sail design entries fetched from the
 * WordPress REST API (pirate-gallery.php mu-plugin).
 *
 * Also provides `addSubmission` used by the submit page to POST a new entry.
 * Image data is sent server-side; the server saves it and returns a URL.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApprovedFlags, submitFlag } from '../services/pirateGalleryService';

const DesignSubmissionsContext = createContext();

export const DesignSubmissionsProvider = ({ children }) => {
  const [approved, setApproved] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchApproved = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getApprovedFlags();
      setApproved(data);
    } catch (err) {
      console.error('[PirateGallery] Failed to fetch approved entries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load approved entries on mount
  useEffect(() => {
    fetchApproved();
  }, [fetchApproved]);

  /**
   * Submit a new sail design.  Sends the base64 image data to the server; the server
   * saves the file and stores only a URL.
   *
   * @param {Object} submission  — { childName, age, email, school?, title, description, imageData, imageFileName }
   * @returns {Promise<Object>}  — { id, status, message, childName?, title? }
   */
  const addSubmission = useCallback(async (submission) => {
    const result = await submitFlag(submission);
    // Don't need to refresh approved here: submission starts as 'pending'
    return result;
  }, []);

  return (
    <DesignSubmissionsContext.Provider
      value={{
        approved,
        loading,
        error,
        addSubmission,
        refreshApproved: fetchApproved,
      }}
    >
      {children}
    </DesignSubmissionsContext.Provider>
  );
};

export const useDesignSubmissions = () => {
  const ctx = useContext(DesignSubmissionsContext);
  if (!ctx) {
    throw new Error('useDesignSubmissions must be used within DesignSubmissionsProvider');
  }
  return ctx;
};
